import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { toast } from 'react-toastify';
import Toast from '../../../../../../components/Toast';
import { options } from '../../../../../../helpers/toastOptions';
import { useManager } from '../../../../../../hooks/Manager';
import { useModal } from '../../../../../../hooks/Modal';
import { ModalTitle, ModalText } from './styled';
import { MyCollectionsContext } from '../..';
import api from '../../../../../../services/api';

export const ModalDownloadDocuments = () => {
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [selected, setSelected] = useState([]);
  const [disableDownloadType, setDisableDownloadType] = useState([]);

  const { getPickupManagerBranchDocumentation } = useManager();
  const { closeModal, Modal } = useModal();

  const {
    protocolToModal,
    setProtocolToModal,
    branchParameterizationToModal,
    setBranchParameterizationToModal,
    modalMessage,
    setModalMessage,
    documentListToDownload,
    setDocumentListToDownload
  } = useContext(MyCollectionsContext);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = [];
      documentListToDownload.forEach((value) => {
        if (!disableDownloadType.includes(value.taxDocumentTypeId)) {
          newSelected.push(value.storageUrl);
        }
      });
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (_, storageUrl) => {
    const selectedIndex = selected.indexOf(storageUrl);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, storageUrl);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    }
  }));

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14
    }
  }));

  const handleCloseModal = () => {
    setProtocolToModal('');
    setModalMessage('');
    setBranchParameterizationToModal('');
    setDocumentListToDownload([]);
    closeModal();
  };

  const handleDownload = () => {
    if (
      selected.length === 0 ||
      (selected.length === 1 && selected[0] === null)
    ) {
      toast(
        <Toast
          title="Atenção!"
          content="Selecione ao menos um documento para baixar."
          type="error"
        />,
        options
      );

      return;
    }

    const link = document.createElement('a');

    selected.forEach((storageUrl) => {
      const indexFile = documentListToDownload.findIndex(
        (document) => document.storageUrl === storageUrl
      );

      const { storageUrl: url, name } = documentListToDownload[indexFile];

      api.get(url, { responseType: 'arraybuffer' }).then((response) => {
        const type = response.headers['content-type'];

        const blob = new Blob([response.data], { type });

        link.href = URL.createObjectURL(blob);
        link.download = decodeURI(name);

        link.click();
      });
    });

    link.remove();

    handleCloseModal();
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  const onComponentDidMount = async () => {
    if (documentListToDownload?.length === 0) {
      setModalMessage('Sem documentos');
      return;
    }

    const newDocumentListToDownload = documentListToDownload
      .filter((value) => !!value.storageUrl)
      .map((data) => {
        const urlSplit = data.storageUrl.split('?')[0].split('/');

        const name = urlSplit[urlSplit.length - 1];

        data.name = name;

        return data;
      });

    setDocumentListToDownload(newDocumentListToDownload);

    try {
      const documentationResponse = await getPickupManagerBranchDocumentation({
        branchParameterizationId: branchParameterizationToModal
      });
      const newDisableDownloadType = [];

      if (documentationResponse?.length > 0) {
        const [downloadConfig] = documentationResponse;

        !downloadConfig?.enableAuthorizationLetterDownload &&
          newDisableDownloadType.push(1);

        !downloadConfig?.enableFailureLetterDownload &&
          newDisableDownloadType.push(2);

        !downloadConfig?.enableDeclarationDownload &&
          newDisableDownloadType.push(3);

        !downloadConfig?.enableFispqDownload && newDisableDownloadType.push(4);
      }

      setDisableDownloadType(newDisableDownloadType);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  useEffect(() => {
    onComponentDidMount();
  }, []);

  return (
    <Modal preventClose>
      <Stack spacing={2}>
        <ModalTitle>Outros Documentos</ModalTitle>

        <ModalText>Protocolo: {protocolToModal}</ModalText>

        <TableContainer component={Paper}>
          <Table stickyHeader size="small">
            <TableHead>
              <StyledTableRow>
                {!isLoadingDetails && documentListToDownload.length > 0 && (
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      color="secondary"
                      indeterminate={
                        selected.length > 0 &&
                        selected.length <
                          documentListToDownload?.filter(
                            (value) =>
                              !disableDownloadType.includes(
                                value.taxDocumentTypeId
                              )
                          )?.length
                      }
                      checked={
                        documentListToDownload?.filter(
                          (value) =>
                            !disableDownloadType.includes(
                              value.taxDocumentTypeId
                            )
                        )?.length > 0 &&
                        selected.length ===
                          documentListToDownload?.filter(
                            (value) =>
                              !disableDownloadType.includes(
                                value.taxDocumentTypeId
                              )
                          )?.length
                      }
                      onChange={handleSelectAllClick}
                    />
                  </StyledTableCell>
                )}
                <StyledTableCell style={{ textAlign: 'center' }}>
                  Documento
                </StyledTableCell>
                <StyledTableCell
                  style={{ textAlign: 'center', whiteSpace: 'nowrap' }}
                >
                  Tipo de Documento
                </StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {(isLoadingDetails || documentListToDownload?.length === 0) && (
                <StyledTableRow>
                  <StyledTableCell
                    colSpan={3}
                    style={{ textAlign: 'center', color: '#888' }}
                  >
                    {modalMessage}
                  </StyledTableCell>
                </StyledTableRow>
              )}
              {!isLoadingDetails && documentListToDownload?.length > 0 && (
                <>
                  {documentListToDownload
                    .filter((value) => !!value.storageUrl)
                    .map((row) => {
                      const isItemSelected = isSelected(row.storageUrl);
                      const labelId = `table-checkbox-${row.taxDocumentId}`;

                      return (
                        <StyledTableRow
                          key={row.taxDocumentId}
                          onClick={(event) => {
                            if (
                              disableDownloadType.includes(
                                row.taxDocumentTypeId
                              )
                            ) {
                              return;
                            }
                            handleClick(event, row.storageUrl);
                          }}
                          style={{
                            cursor: !disableDownloadType.includes(
                              row.taxDocumentTypeId
                            )
                              ? 'pointer'
                              : 'default'
                          }}
                        >
                          <StyledTableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isItemSelected}
                              inputProps={{ 'aria-labelledby': labelId }}
                              disabled={disableDownloadType.includes(
                                row.taxDocumentTypeId
                              )}
                            />
                          </StyledTableCell>
                          <StyledTableCell style={{ textAlign: 'center' }}>
                            {row.name}
                          </StyledTableCell>
                          <StyledTableCell style={{ textAlign: 'center' }}>
                            {row.taxDocumentType || '-'}
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 32
          }}
        >
          <Button
            type="button"
            variant="contained"
            color="secondary"
            onClick={handleCloseModal}
          >
            Voltar
          </Button>
          <Button type="button" variant="contained" onClick={handleDownload}>
            Baixar
          </Button>
        </Box>
      </Stack>
    </Modal>
  );
};
